// Analytics data types for Green P100NEERS app

// Seasonal Insights types
export interface SeasonalAverage {
  Season: string;
  KWH: number;
}

export interface HourlySeasonalData {
  hour: number;
  time: string;
  Winter: number;
  Spring: number;
  Summer: number;
  Fall: number;
}

export interface BusinessHoursBySeasonData {
  season: string;
  business: number;
  nonBusiness: number;
}

export interface WeeklyHeatmapItem {
  day: string;
  hour: number;
  value: number;
}

export interface SeasonBreakdownItem {
  season: string;
  kwh: number;
  percentage: number;
}

export interface PeakHourData {
  hour: number;
  kwh: number;
}

// New hourly profile data structure
export interface HourlyProfileItem {
  hour: number;
  avg_kwh: number;
}

// New business hours analysis structure
export interface BusinessHoursAnalysis {
  operational_avg_kwh: number;
  non_operational_avg_kwh: number;
  ratio: number;
}

// New season analysis structure
export interface SeasonAnalysis {
  total_kwh: number;
  average_kwh: number; // Average per day
  peak_hour_info: {
    peak_hour: number;
    avg_kwh_at_peak: number;
  };
  avg_hourly_profile: HourlyProfileItem[];
  weekly_heatmap_data: {
    [day: string]: HourlyProfileItem[];
  };
  business_hours_analysis: BusinessHoursAnalysis;
}

export interface SeasonalInsightsData {
  // New data structure
  annual_total_kwh: number;
  analysis_by_season: {
    [season: string]: SeasonAnalysis;
  };
  
  // Keeping backward compatibility
  seasonalAverages: SeasonalAverage[];
  hourlyBySeasonData: HourlySeasonalData[];
  businessHoursBySeason: BusinessHoursBySeasonData[];
  weeklyHeatmapBySeason: {
    [season: string]: WeeklyHeatmapItem[];
  };
  seasonBreakdown: SeasonBreakdownItem[];
  peakHoursBySeason: {
    [season: string]: PeakHourData;
  };
  periodCovered: {
    start: string;
    end: string;
  };
  insights: string[];
}

// Efficiency Metrics types
export interface MonthlyEUI {
  year: number;
  month: number;
  kwh: number;
  eui: number;
}

export interface AnnualEUI {
  year: number;
  kwh: number;
  eui: number;
}

export interface BaselineDayData {
  day: string;
  kwhBaseline: number;
}

export interface MonthlyOperationalData {
  year: number;
  month: number;
  operational: number;
  nonOperational: number;
}

export interface PeakHourDistribution {
  hour: number;
  count: number;
  percentage: number;
}

export interface TopPeakEvent {
  date: string;
  time: string;
  kwh: number;
}

export interface EfficiencyScoreComponents {
  baselineEfficiency: number;
  operationalRatio: number;
  peakDemandManagement: number;
  timeOfUseOptimization: number;
}

export interface Recommendation {
  category: string;
  title: string;
  description: string;
  potential_impact: string;
  estimated_savings: string;
}

export interface EfficiencyMetricsData {
  energyIntensity: {
    monthly: MonthlyEUI[];
    annual: AnnualEUI[];
    floorAreaSqFt: number;
  };
  baselineAnalysis: {
    averageBaselineKwh: number;
    nonOperationalPercentage: number;
    byDayOfWeek: BaselineDayData[];
  };
  operationalUsage: {
    avgBusinessHoursKwh: number;
    avgNonBusinessHoursKwh: number;
    usageRatio: number;
    monthlyBreakdown: MonthlyOperationalData[];
  };
  peakDemand: {
    hourlyDistribution: PeakHourDistribution[];
    topPeakEvents: TopPeakEvent[];
    potentialReductionKwh: number;
  };
  efficiencyScore: {
    overall: number;
    grade: string;
    components: EfficiencyScoreComponents;
  };
  recommendations: Recommendation[];
  periodCovered: {
    start: string;
    end: string;
  };
}

// Anomaly Detection types
export interface DailyAnomaly {
  date: string;
  dayOfWeek: string;
  totalKwh: number;
  expectedKwh: number;
  deviation: number;
  deviationPercent: number;
  reason: string;
  severity: string;
}

export interface HourlyAnomaly {
  date: string;
  time: string;
  kwh: number;
  expectedKwh: number;
  deviation: number;
  deviationPercent: number;
  isBusinessHours: boolean;
  dayOfWeek: string;
  hour: number;
  severity: string;
}

export interface AnomalyRecommendation {
  title: string;
  description: string;
  potentialSavings: string;
}

export interface AnomalyData {
  dailyAnomalies: DailyAnomaly[];
  hourlyAnomalies: HourlyAnomaly[];
  insights: string[];
  recommendations: AnomalyRecommendation[];
  anomalyStats: {
    totalDailyAnomalies: number;
    totalHourlyAnomalies: number;
    percentDaysWithAnomalies: number;
    percentHoursWithAnomalies: number;
  };
  periodCovered: {
    start: string;
    end: string;
  };
}

// Add type definition for operational hours
export interface OperationalHourSchedule {
  day: string;
  start: string | null;
  end: string | null;
  isOpen: boolean;
}

export interface OperationalHoursData {
  schedule: {
    [dayIndex: string]: OperationalHourSchedule;
  };
  timezone: string;
}

// Add function to fetch operational hours
export const getOperationalHours = async (): Promise<OperationalHoursData> => {
  try {
    const response = await fetch('/data/operational_hours.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch operational hours data: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data as OperationalHoursData;
  } catch (error) {
    console.error('Error fetching operational hours data:', error);
    throw error;
  }
};

// Service functions to fetch the data
export const getSeasonalInsights = async (businessId: string): Promise<SeasonalInsightsData> => {
  try {
    // Only support BIZ001 for now
    if (businessId !== 'BIZ001') {
      throw new Error('Seasonal insights data only available for BIZ001');
    }
    
    // Fetch the JSON data from the public data directory
    const response = await fetch('/data/seasonal_insights_BIZ001.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch seasonal insights data: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data as SeasonalInsightsData;
  } catch (error) {
    console.error('Error fetching seasonal insights data:', error);
    throw error;
  }
};

export const getEfficiencyMetrics = async (businessId: string): Promise<EfficiencyMetricsData> => {
  try {
    // Only support BIZ001 for now
    if (businessId !== 'BIZ001') {
      throw new Error('Efficiency metrics data only available for BIZ001');
    }
    
    // Fetch the JSON data from the public data directory
    const response = await fetch('/data/efficiency_metrics_BIZ001.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch efficiency metrics data: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data as EfficiencyMetricsData;
  } catch (error) {
    console.error('Error fetching efficiency metrics data:', error);
    throw error;
  }
};

export const getAnomalyData = async (businessId: string): Promise<AnomalyData> => {
  try {
    // Only support BIZ001 for now
    if (businessId !== 'BIZ001') {
      throw new Error('Anomaly data only available for BIZ001');
    }
    
    // Fetch the JSON data from the public data directory
    const response = await fetch('/data/anomalies_BIZ001.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch anomaly data: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data as AnomalyData;
  } catch (error) {
    console.error('Error fetching anomaly data:', error);
    throw error;
  }
}; 